/**
 * Client-side Product Tracking
 *
 * Stores recently viewed product IDs and product configurations in localStorage.
 * Data persists until browser data is cleared.
 */

const PRODUCTS_KEY = 'hive_recently_viewed_products'
const CATEGORIES_KEY = 'hive_recently_viewed_categories'
const CONFIGURATIONS_KEY = 'hive_product_configurations'
const MAX_ITEMS = 12
const MAX_CONFIGURATIONS = 50

// Categories to exclude from recently viewed
const EXCLUDED_CATEGORY_IDS = []

/**
 * Add a product to recently viewed (stores ID only)
 * Call this when a product page loads
 */
export function addRecentlyViewedProduct(product) {
    if (typeof window === 'undefined' || !product?.sub_product_id) return

    const sub_product_id = product.sub_product_id
    const items = getRecentlyViewedProductIds()

    // Remove if already exists (will re-add at front)
    const filtered = items.filter(id => id !== sub_product_id)

    // Add to front, limit to MAX_ITEMS
    const updated = [sub_product_id, ...filtered].slice(0, MAX_ITEMS)

    try {
        localStorage.setItem(PRODUCTS_KEY, JSON.stringify(updated))
    } catch (e) {
        // sessionStorage full or unavailable - silently fail
    }
}

/**
 * Get recently viewed product IDs
 */
export function getRecentlyViewedProductIds() {
    if (typeof window === 'undefined') return []

    try {
        const stored = localStorage.getItem(PRODUCTS_KEY)
        return stored ? JSON.parse(stored) : []
    } catch (e) {
        return []
    }
}

/**
 * Add a category to recently viewed
 * Call this when a category page loads
 */
export function addRecentlyViewedCategory(category) {
    if (typeof window === 'undefined' || !category?.category_id) return

    // Exclude specific categories
    if (EXCLUDED_CATEGORY_IDS.includes(category.category_id)) return

    const item = {
        category_id: category.category_id,
        category_name: category.category_name,
        display_name: category.display_name || category.category_name,
        menu_name: category.nav?.name || category.display_name || category.category_name,
        url: category.url,
        category_photo: category.images?.landing?.file_name || null,
        category_landing: category.images?.landing || null,
        designer: category.designer || null
    }

    const items = getRecentlyViewedCategories()

    // Remove if already exists (will re-add at front)
    const filtered = items.filter(c => c.category_id !== item.category_id)

    // Add to front, limit to MAX_ITEMS
    const updated = [item, ...filtered].slice(0, MAX_ITEMS)

    try {
        localStorage.setItem(CATEGORIES_KEY, JSON.stringify(updated))
    } catch (e) {
        // sessionStorage full or unavailable - silently fail
    }
}

/**
 * Get recently viewed categories
 */
export function getRecentlyViewedCategories() {
    if (typeof window === 'undefined') return []

    try {
        const stored = localStorage.getItem(CATEGORIES_KEY)
        return stored ? JSON.parse(stored) : []
    } catch (e) {
        return []
    }
}

/**
 * Get the last shopping location URL for "Continue Shopping" links
 * Returns the most recent category URL, or null if none exists
 */
export function getLastShoppingLocation() {
    const categories = getRecentlyViewedCategories()
    return categories[0]?.url || null
}

/**
 * Clear all recently viewed data
 */
export function clearRecentlyViewed() {
    if (typeof window === 'undefined') return

    try {
        localStorage.removeItem(PRODUCTS_KEY)
        localStorage.removeItem(CATEGORIES_KEY)
    } catch (e) {
        // silently fail
    }
}

// =============================================================================
// PRODUCT CONFIGURATIONS
// =============================================================================

/**
 * Save product configuration (selected options and swatches)
 * Stores only IDs, not full objects
 */
export function saveProductConfiguration(sub_product_id, options, swatches) {
    if (typeof window === 'undefined' || !sub_product_id) return

    try {
        const configs = getProductConfigurations()

        // Store only IDs
        configs[sub_product_id] = {
            options: options || {},
            swatches: swatches || {},
            timestamp: Date.now()
        }

        // Prune old entries if we have too many
        const entries = Object.entries(configs)
        if (entries.length > MAX_CONFIGURATIONS) {
            // Sort by timestamp, keep most recent
            entries.sort((a, b) => (b[1].timestamp || 0) - (a[1].timestamp || 0))
            const pruned = Object.fromEntries(entries.slice(0, MAX_CONFIGURATIONS))
            localStorage.setItem(CONFIGURATIONS_KEY, JSON.stringify(pruned))
        } else {
            localStorage.setItem(CONFIGURATIONS_KEY, JSON.stringify(configs))
        }
    } catch (e) {
        // localStorage full or unavailable - silently fail
    }
}

/**
 * Get saved configuration for a product
 * Returns { options: {selectionId: optionId}, swatches: {swatchSetId: swatchId} } or null
 */
export function getProductConfiguration(sub_product_id) {
    if (typeof window === 'undefined' || !sub_product_id) return null

    try {
        const configs = getProductConfigurations()
        const config = configs[sub_product_id]
        if (config) {
            return {
                options: config.options || {},
                swatches: config.swatches || {}
            }
        }
        return null
    } catch (e) {
        return null
    }
}

/**
 * Get all product configurations
 */
function getProductConfigurations() {
    if (typeof window === 'undefined') return {}

    try {
        const stored = localStorage.getItem(CONFIGURATIONS_KEY)
        return stored ? JSON.parse(stored) : {}
    } catch (e) {
        return {}
    }
}
